import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Megaphone, AlertTriangle, CheckCircle2, AlertCircle, Info,
  X, ChevronLeft, ChevronRight, Sparkles, Tag
} from 'lucide-react';
import { db, collection, getDocs, query, where, doc, cachedGetDoc, cachedGetDocs } from '../services/firebase';

// Allowed pages where announcements should appear (Exclusively Retailer Home)
const ALLOWED_ROUTES = ['/portal', '/'];

const TYPE_CONFIG = {
  urgent: {
    label: 'Important Alert',
    badge: 'Urgent',
    icon: AlertCircle,
    wrap: 'bg-red-50 border-red-200',
    badgeStyle: 'bg-red-600 text-white shadow-xs',
    textStyle: 'text-red-950',
    iconStyle: 'text-red-600',
  },
  warning: {
    label: 'Operational Notice',
    badge: 'Notice',
    icon: AlertTriangle,
    wrap: 'bg-amber-50 border-amber-200',
    badgeStyle: 'bg-amber-600 text-white shadow-xs',
    textStyle: 'text-amber-950',
    iconStyle: 'text-amber-600',
  },
  success: {
    label: 'Special Offer & Update',
    badge: 'Offer / Update',
    icon: Tag,
    wrap: 'bg-emerald-50 border-emerald-200',
    badgeStyle: 'bg-emerald-600 text-white shadow-xs',
    textStyle: 'text-emerald-950',
    iconStyle: 'text-emerald-600',
  },
  info: {
    label: 'Notice & Announcement',
    badge: 'Bulletin',
    icon: Megaphone,
    wrap: 'bg-[#1e3a5f]/10 border-[#1e3a5f]/20',
    badgeStyle: 'bg-[#1e3a5f] text-white shadow-xs',
    textStyle: 'text-[#0f1f33]',
    iconStyle: 'text-[#1e3a5f]',
  },
};

export default function AnnouncementSlider() {
  const location = useLocation();
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [slideDirection, setSlideDirection] = useState(1); // 1 = next, -1 = prev

  // Read dismissed banners from localStorage
  const getDismissed = () => {
    try {
      return JSON.parse(localStorage.getItem('lg_dismissed_announcements') || '[]');
    } catch {
      return [];
    }
  };

  // Only show strictly on Retailer Home page, and never if user account is blocked
  const isAllowedPage = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('lg_user') || '{}');
      if (user.blocked) return false;
    } catch {}
    const p = location.pathname.replace(/\/+$/, '') || '/';
    return p === '/portal' || p === '/';
  }, [location.pathname]);

  // Fetch active announcements from Firestore
  useEffect(() => {
    if (!isAllowedPage) return;

    let mounted = true;
    async function fetchAnnouncements() {
      try {
        const dismissed = getDismissed();
        const now = new Date();

        // 1. Fetch from announcements collection
        const annSnap = await cachedGetDocs(
          query(collection(db, 'announcements'), where('target', 'in', ['all', 'retailers'])),
          'announcements_retailers',
          3 * 60 * 1000
        );

        let activeItems = annSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // 2. Fallback to settings/banner if announcements collection has no docs
        if (activeItems.length === 0) {
          const bannerDoc = await cachedGetDoc(doc(db, 'settings', 'banner'), 3 * 60 * 1000);
          if (bannerDoc.exists()) {
            const bData = bannerDoc.data();
            if (bData.active && (bData.target === 'all' || bData.target === 'retailers')) {
              activeItems = [{ id: 'settings_banner', ...bData }];
            }
          }
        }

        // Filter out expired & dismissed items
        const valid = activeItems.filter(item => {
          if (!item.message) return false;
          if (item.expiresAt && new Date(item.expiresAt) < now) return false;
          const key = item.id || item.createdAt;
          if (dismissed.includes(key)) return false;
          return true;
        });

        // Sort: newest first
        valid.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

        if (mounted) {
          setBanners(valid);
          setCurrentIndex(0);
        }
      } catch (err) {
        console.error('Error fetching announcements slider:', err);
      }
    }

    fetchAnnouncements();
    return () => { mounted = false; };
  }, [isAllowedPage, location.pathname]);

  // Auto-slide every 5 seconds if multiple banners exist and user is not hovering
  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setSlideDirection(1);
      setCurrentIndex(prev => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [banners.length, isPaused]);

  // If not on allowed page or no active banners, render nothing
  if (!isAllowedPage || banners.length === 0) {
    return null;
  }

  const current = banners[currentIndex] || banners[0];
  const conf = TYPE_CONFIG[current.type] || TYPE_CONFIG.info;
  const Icon = conf.icon;

  const handleNext = (e) => {
    e?.stopPropagation();
    setSlideDirection(1);
    setCurrentIndex(prev => (prev + 1) % banners.length);
  };

  const handlePrev = (e) => {
    e?.stopPropagation();
    setSlideDirection(-1);
    setCurrentIndex(prev => (prev - 1 + banners.length) % banners.length);
  };

  const handleDismiss = (e) => {
    e?.stopPropagation();
    const key = current.id || current.createdAt;
    const dismissed = getDismissed();
    if (!dismissed.includes(key)) {
      dismissed.push(key);
      localStorage.setItem('lg_dismissed_announcements', JSON.stringify(dismissed));
    }
    const updated = banners.filter((_, idx) => idx !== currentIndex);
    setBanners(updated);
    if (currentIndex >= updated.length) {
      setCurrentIndex(Math.max(0, updated.length - 1));
    }
  };

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 35 : -35,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -35 : 35,
      opacity: 0,
    }),
  };

  return (
    <div
      className="mb-3.5 sm:mb-4 w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <div
        className={`relative overflow-hidden rounded-xl sm:rounded-2xl border p-3 sm:p-3.5 shadow-2xs transition-all ${conf.wrap}`}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Main Slide Content */}
          <div className="flex items-start gap-2.5 sm:gap-3 flex-1 min-w-0">
            {/* Type Icon */}
            <div className="mt-0.5 shrink-0 flex items-center justify-center">
              <Icon size={18} className={conf.iconStyle} />
            </div>

            {/* Slide Body */}
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${conf.badgeStyle}`}
                >
                  {current.badge || conf.badge}
                </span>

                {banners.length > 1 && (
                  <span className="text-[10px] font-mono text-[#78716c] dark:text-[#a8a49c] font-semibold">
                    {currentIndex + 1} of {banners.length}
                  </span>
                )}
              </div>

              <div className="relative min-h-[22px]">
                <AnimatePresence mode="wait" custom={slideDirection}>
                  <motion.p
                    key={current.id || currentIndex}
                    custom={slideDirection}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className={`text-xs sm:text-sm font-medium leading-relaxed ${conf.textStyle} break-words`}
                  >
                    {current.message}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Right Action Controls: Arrows & Dismiss Button */}
          <div className="flex items-center gap-1 shrink-0 mt-0.5">
            {banners.length > 1 && (
              <div className="flex items-center gap-0.5 mr-1 hidden sm:flex">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-[#78716c] hover:text-[#1a1917] hover:bg-black/5 transition-colors cursor-pointer"
                  title="Previous banner"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-[#78716c] hover:text-[#1a1917] hover:bg-black/5 transition-colors cursor-pointer"
                  title="Next banner"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}

            {/* Dismiss Cross */}
            <button
              type="button"
              onClick={handleDismiss}
              className="w-6 h-6 rounded-md flex items-center justify-center text-[#78716c] hover:text-[#1a1917] hover:bg-black/5 transition-colors cursor-pointer"
              title="Dismiss this announcement"
            >
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Carousel Pagination Dots (Only if multiple banners) */}
        {banners.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 pt-2.5 mt-2 border-t border-black/5">
            {banners.map((b, idx) => (
              <button
                key={b.id || idx}
                type="button"
                onClick={() => {
                  setSlideDirection(idx > currentIndex ? 1 : -1);
                  setCurrentIndex(idx);
                }}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  idx === currentIndex
                    ? 'w-5 bg-[#1e3a5f]'
                    : 'w-1.5 bg-black/15 hover:bg-black/30'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
